import { type CSSProperties, type Ref, useMemo } from 'react';

import { Box } from '@opendoor/atoms/box';
import { cn, mergeRefs } from '@opendoor/atoms/utils';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  type GalleryBreakpointTier,
  generateGalleryPictureSet,
} from 'components/marketplace/helpers/presizedPhotos';

import { PropertyPhotoPlaceholder, usePropertyPhotoLoadState } from './PropertyPhoto';

export type GalleryListingPhotoProps = {
  photo: GalleryPhoto;
  sizes: GalleryBreakpointTier[];
  alt: string;
  lazy?: boolean;
  preload?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
  objectFit?: CSSProperties['objectFit'];
  className?: string;
  imageRef?: Ref<HTMLImageElement>;
  fit?: 'cover' | 'contain';
};

export const GalleryListingPhoto = ({
  photo,
  sizes,
  alt,
  lazy = true,
  preload = false,
  fetchPriority,
  objectFit = 'cover',
  className,
  imageRef,
  fit,
}: GalleryListingPhotoProps) => {
  const sourceKey = [photo.url, photo.urlXs, photo.urlSm, photo.urlLg, photo.urlXl]
    .filter(Boolean)
    .join('|');
  const {
    imageRef: loadStateRef,
    state,
    onLoad,
    onError,
  } = usePropertyPhotoLoadState(sourceKey || null);
  const mergedImageRef = useMemo(() => mergeRefs(loadStateRef, imageRef), [imageRef, loadStateRef]);

  return (
    <Box className={cn('relative h-full w-full overflow-hidden', className)}>
      {generateGalleryPictureSet({
        photo,
        sizes,
        lazy,
        preload,
        fetchPriority,
        alt,
        objectFit,
        imageRef: mergedImageRef,
        onLoad,
        onError,
        fit,
      })}
      {state !== 'loaded' && (
        <PropertyPhotoPlaceholder state={state} className="absolute inset-0" />
      )}
    </Box>
  );
};
